import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Receipt,
  FileText,
  Calendar,
  Truck,
  Users,
  Building2,
  ChevronRight,
  Search,
  RefreshCw,
  ClipboardList,
  Briefcase,
  AlertCircle,
  Clock,
  DollarSign
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function InvoiceReviewPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);          // ALL jobs with at least one ticket
  const [tickets, setTickets] = useState([]);
  const [search, setSearch] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState('pending');     // 'pending' | 'invoiced' | 'all'

  // Helper: classify a job as having any ticket / any invoice
  const hasAnyTicket = (job) =>
    !!(job.client_ticket_number ||
      (job.client_ticket_numbers && Object.values(job.client_ticket_numbers).some(t => t)));
  const hasAnyInvoice = (job) =>
    !!(job.invoice_number ||
      (job.invoice_numbers && Object.values(job.invoice_numbers).some(i => i)));

  const fetchData = async () => {
    const token = localStorage.getItem('vaultai_token');
    try {
      // Fetch ALL jobs (active AND inactive) — invoicing still happens for
      // archived/closed jobs. Default would exclude inactive ones.
      const jobsResponse = await axios.get(`${API}/dispatch/jobs?active_only=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Fetch a generous number of client tickets so dates resolve for older jobs too
      const ticketsResponse = await axios.get(`${API}/client-tickets?limit=1000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (jobsResponse.data.success) {
        // Keep every job that has at least one ticket. The pending/invoiced
        // partition is derived at render time via the tab state.
        const withTicket = (jobsResponse.data.jobs || []).filter(hasAnyTicket);
        setJobs(withTicket);
      }
      
      if (ticketsResponse.data.success) {
        setTickets(ticketsResponse.data.tickets || []);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load invoice review data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  // Get the ticket date for a job (from actual client ticket, not assignments)
  const getTicketDateForJob = (job) => {
    // Get all ticket numbers for this job
    const ticketNumbers = [];
    if (job.client_ticket_number) {
      ticketNumbers.push(job.client_ticket_number);
    }
    if (job.client_ticket_numbers) {
      Object.values(job.client_ticket_numbers).forEach(t => {
        if (t) ticketNumbers.push(t);
      });
    }
    
    // Find matching tickets and get their dates
    const matchingTickets = tickets.filter(t => ticketNumbers.includes(t.ticket_number));
    if (matchingTickets.length > 0) {
      // Return the most recent ticket's date
      const dates = matchingTickets.map(t => t.date).filter(Boolean).sort().reverse();
      return dates[0] || null;
    }
    
    // Fallback to job's created_at date
    return job.created_at?.split('T')[0] || null;
  };

  // Group jobs by ticket date (oldest first), filtered by the active tab.
  const getGroupedJobs = () => {
    const jobDateMap = new Map();

    const visibleJobs = jobs.filter(job => {
      if (tab === 'pending') return !hasAnyInvoice(job);
      if (tab === 'invoiced') return hasAnyInvoice(job);
      return true; // 'all'
    });

    visibleJobs.forEach(job => {
      const ticketDate = getTicketDateForJob(job);
      jobDateMap.set(job.id, { job, date: ticketDate || 'unknown' });
    });

    // Group by date
    const grouped = {};
    jobDateMap.forEach(({ job, date }) => {
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push({ job });
    });

    // Sort dates oldest first (or newest first for invoiced view — feels more natural)
    const sortedDates = Object.keys(grouped).sort((a, b) => {
      if (a === 'unknown') return 1;
      if (b === 'unknown') return -1;
      return tab === 'invoiced' ? b.localeCompare(a) : a.localeCompare(b);
    });

    return { grouped, sortedDates };
  };

  // Filter by search
  const filterJobs = (jobList) => {
    if (!search) return jobList;
    const searchLower = search.toLowerCase();
    return jobList.filter(({ job }) => {
      // Check basic fields
      if (job.job_number?.toLowerCase().includes(searchLower)) return true;
      if (job.client_name?.toLowerCase().includes(searchLower)) return true;
      if (job.site_name?.toLowerCase().includes(searchLower)) return true;
      // Check legacy ticket number
      if (job.client_ticket_number?.toLowerCase().includes(searchLower)) return true;
      // Check per-product ticket numbers
      if (job.client_ticket_numbers) {
        for (const ticketNum of Object.values(job.client_ticket_numbers)) {
          if (ticketNum?.toLowerCase().includes(searchLower)) return true;
        }
      }
      return false;
    });
  };

  // Get ticket numbers for display
  const getTicketNumbers = (job) => {
    const numbers = [];
    if (job.client_ticket_number) numbers.push(job.client_ticket_number);
    if (job.client_ticket_numbers) {
      Object.entries(job.client_ticket_numbers).forEach(([type, num]) => {
        if (num && !numbers.includes(num)) {
          numbers.push(`${num} (${type})`);
        }
      });
    }
    return numbers;
  };

  // Get invoice numbers for display (mirror of getTicketNumbers)
  const getInvoiceNumbers = (job) => {
    const numbers = [];
    if (job.invoice_number) numbers.push(job.invoice_number);
    if (job.invoice_numbers) {
      Object.entries(job.invoice_numbers).forEach(([type, num]) => {
        if (num && !numbers.includes(num)) {
          numbers.push(`${num} (${type})`);
        }
      });
    }
    return numbers;
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === 'unknown') return 'Unknown Date';
    try {
      const date = new Date(dateStr + 'T00:00:00');
      return date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  // Calculate days ago
  const getDaysAgo = (dateStr) => {
    if (!dateStr || dateStr === 'unknown') return null;
    try {
      const date = new Date(dateStr);
      const today = new Date();
      const diffTime = today - date;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return '1 day ago';
      return `${diffDays} days ago`;
    } catch {
      return null;
    }
  };

  // Navigate to dispatch board on the ticket's date
  const handleJobClick = (job) => {
    const ticketDate = getTicketDateForJob(job);
    if (ticketDate && ticketDate !== 'unknown') {
      navigate(`/dashboard/dispatch?date=${ticketDate}`);
    } else {
      navigate('/dashboard/dispatch');
    }
  };

  const { grouped, sortedDates } = getGroupedJobs();
  // Counts for each tab
  const pendingCount = jobs.filter(j => !hasAnyInvoice(j)).length;
  const invoicedCount = jobs.filter(j => hasAnyInvoice(j)).length;
  const totalAllCount = jobs.length;
  const visibleCount = tab === 'pending' ? pendingCount
    : tab === 'invoiced' ? invoicedCount
    : totalAllCount;

  return (
    <div className="p-6 space-y-6" data-testid="invoice-review-page">
      {/* Header with navigation tabs */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-500" />
            Invoice Review
          </h1>
          <div className="flex gap-1">
            <Link to="/dashboard/dispatch">
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
                <ClipboardList className="h-4 w-4 mr-1" />
                Dispatch Board
              </Button>
            </Link>
            <Link to="/dashboard/dispatch/jobs">
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
                <Briefcase className="h-4 w-4 mr-1" />
                Jobs
              </Button>
            </Link>
            <Link to="/dashboard/dispatch/drivers">
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
                <Users className="h-4 w-4 mr-1" />
                Operators
              </Button>
            </Link>
            <Link to="/dashboard/dispatch/units">
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
                <Truck className="h-4 w-4 mr-1" />
                Units
              </Button>
            </Link>
            <Link to="/dashboard/dispatch/invoice-review">
              <Button variant="ghost" size="sm" className="bg-secondary text-foreground">
                <DollarSign className="h-4 w-4 mr-1" />
                Invoice Review
              </Button>
            </Link>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search jobs, clients, tickets..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-64"
            />
          </div>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Tabs: Pending / Invoiced / All */}
      <div className="flex items-center gap-1 border-b border-border">
        {[
          { id: 'pending',  label: 'Pending Invoice', count: pendingCount,  color: 'text-amber-500' },
          { id: 'invoiced', label: 'Invoiced',        count: invoicedCount, color: 'text-green-500' },
          { id: 'all',      label: 'All',             count: totalAllCount, color: 'text-blue-500' },
        ].map(t => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.id
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`invoice-review-tab-${t.id}`}
          >
            {t.label}
            <span className={`ml-2 text-xs ${t.color}`}>{t.count}</span>
          </button>
        ))}
      </div>

      {/* Summary Card */}
      <Card className={
        tab === 'invoiced'
          ? "bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30"
          : tab === 'all'
            ? "bg-gradient-to-r from-blue-500/10 to-sky-500/10 border-blue-500/30"
            : "bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/30"
      }>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-lg ${
                tab === 'invoiced' ? 'bg-green-500/20'
                  : tab === 'all' ? 'bg-blue-500/20'
                  : 'bg-amber-500/20'
              }`}>
                {tab === 'invoiced'
                  ? <Receipt className="h-6 w-6 text-green-500" />
                  : tab === 'all'
                    ? <DollarSign className="h-6 w-6 text-blue-500" />
                    : <AlertCircle className="h-6 w-6 text-amber-500" />}
              </div>
              <div>
                <h2 className="text-lg font-semibold">
                  {tab === 'invoiced' ? 'Invoiced Tickets'
                    : tab === 'all' ? 'All Tickets'
                    : 'Pending Invoice Entry'}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {tab === 'invoiced'
                    ? 'Jobs with client tickets that have invoice numbers'
                    : tab === 'all'
                      ? 'All jobs with at least one client ticket (any status)'
                      : 'Jobs with client tickets that need invoice numbers'}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className={`text-3xl font-bold ${
                tab === 'invoiced' ? 'text-green-500'
                  : tab === 'all' ? 'text-blue-500'
                  : 'text-amber-500'
              }`}>{visibleCount}</div>
              <div className="text-sm text-muted-foreground">
                {tab === 'invoiced' ? 'invoiced tickets'
                  : tab === 'all' ? 'tickets total'
                  : 'tickets awaiting invoice'}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Job List by Date */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : visibleCount === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="p-4 bg-green-500/20 rounded-full">
                <Receipt className="h-8 w-8 text-green-500" />
              </div>
              <h3 className="text-lg font-medium">
                {tab === 'pending' ? 'All Caught Up!'
                  : tab === 'invoiced' ? 'No Invoiced Tickets Yet'
                  : 'No Tickets Found'}
              </h3>
              <p className="text-muted-foreground">
                {tab === 'pending'
                  ? 'All client tickets have invoice numbers entered.'
                  : tab === 'invoiced'
                    ? 'Once you enter an invoice number on a ticket, it will appear here.'
                    : 'No jobs with client tickets exist yet.'}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {sortedDates.map(date => {
            const dateJobs = filterJobs(grouped[date]);
            if (dateJobs.length === 0) return null;
            
            const daysAgo = getDaysAgo(date);
            
            return (
              <div key={date} className="space-y-3">
                {/* Date Header */}
                <div className="flex items-center gap-3 sticky top-0 bg-background/95 backdrop-blur py-2 z-10">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Calendar className="h-4 w-4 text-blue-500" />
                    <span>{formatDate(date)}</span>
                  </div>
                  {daysAgo && (
                    <Badge variant="outline" className="text-xs">
                      <Clock className="h-3 w-3 mr-1" />
                      {daysAgo}
                    </Badge>
                  )}
                  <Badge variant="secondary">{dateJobs.length} job{dateJobs.length !== 1 ? 's' : ''}</Badge>
                  <div className="flex-1 h-px bg-border" />
                </div>
                
                {/* Jobs for this date */}
                <div className="grid gap-3">
                  {dateJobs.map(({ job }) => {
                    const ticketNums = getTicketNumbers(job);
                    const invoiceNums = getInvoiceNumbers(job);
                    return (
                    <Card 
                      key={job.id} 
                      className="hover:bg-secondary/30 transition-colors cursor-pointer group"
                      onClick={() => handleJobClick(job)}
                      data-testid={`invoice-job-${job.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 space-y-2">
                            {/* Job Header */}
                            <div className="flex items-center gap-3">
                              <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                                Job #{job.job_number}
                              </Badge>
                              <span className="font-semibold">{job.client_name}</span>
                              {job.site_name && (
                                <span className="text-sm text-muted-foreground">
                                  • {job.site_name}
                                </span>
                              )}
                            </div>
                            
                            {/* Ticket Info */}
                            <div className="flex items-center gap-6 text-sm flex-wrap">
                              <div className="flex items-center gap-2">
                                <Receipt className="h-4 w-4 text-green-500" />
                                <span className="text-muted-foreground">Ticket:</span>
                                <span className="font-medium text-green-400">
                                  {ticketNums.length > 0 ? ticketNums.join(', ') : '-'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <DollarSign className="h-4 w-4 text-amber-500" />
                                <span className="text-muted-foreground">Invoice:</span>
                                {invoiceNums.length > 0 ? (
                                  <span className="font-medium text-green-400">
                                    {invoiceNums.join(', ')}
                                  </span>
                                ) : (
                                  <span className="text-amber-400 italic">Not entered</span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Action Arrow */}
                          <div className="flex items-center gap-2 text-muted-foreground group-hover:text-foreground transition-colors">
                            <span className="text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                              Open in Dispatch Board
                            </span>
                            <ChevronRight className="h-5 w-5" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )})}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
